import type { MockSectionCustomerInput } from './mockSectionData.util';
import { mockSectionData } from './mockSectionData.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { pickReportFieldValue } from './reportFieldSource.util';
import {
  coerceIllustrativeDistributionFromPctRows,
  coerceWhoWereReachingDistribution,
} from './whoWereReachingDistribution.util';
import {
  coerceWhoWereReachingCounties,
  isLikelyMockWhoWereReachingCounties,
  isWarehouseWhoWereReachingSnapshot,
} from './whoWereReachingSnapshotDetect.util';

/** County row in the who-were-reaching snapshot. */
export interface WhoWereReachingCountySnapshot {
  name: string;
  memberCount: number;
}

const hasCountyData = (
  counties: WhoWereReachingCountySnapshot[],
): boolean => counties.some(county => county.memberCount > 0);

const hasDistributionData = (
  rows: Array<{ value: number }>,
): boolean => rows.some(row => row.value > 0);

/**
 * Normalizes stored who-were-reaching snapshots.
 * Warehouse snapshots keep empty fields empty so mock figures cannot look like
 * customer data. Draft (non-warehouse) snapshots still use illustrative mock.
 * @param snapshot - Stored or live snapshot JSON.
 * @param customer - Customer context for mock fallback fields.
 * @param reportingPeriod - Report period label.
 * @returns Snapshot with merged data and per-field `fieldSources` metadata.
 */
export const normalizeWhoWereReachingSnapshot = (
  snapshot: Record<string, unknown>,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const mockBase = mockSectionData(
    'who-were-reaching',
    customer,
    reportingPeriod,
  );
  const isWarehouse = isWarehouseWhoWereReachingSnapshot(snapshot);

  const usStateCode =
    typeof snapshot.usStateCode === 'string'
      ? snapshot.usStateCode
      : customer.usStateCode ?? mockBase.usStateCode;

  const snapshotEngagedTotal =
    typeof snapshot.engagedTotal === 'number' &&
    Number.isFinite(snapshot.engagedTotal)
      ? snapshot.engagedTotal
      : null;

  const stateCodeForCounties =
    typeof usStateCode === 'string' ? usStateCode : customer.usStateCode ?? 'MI';

  const coercedCounties = coerceWhoWereReachingCounties(snapshot.counties);
  const mockCounties = coerceWhoWereReachingCounties(mockBase.counties);

  const warehouseCounties = isWarehouse
    ? coercedCounties
    : coercedCounties.length > 0 &&
        !isLikelyMockWhoWereReachingCounties(
          coercedCounties,
          stateCodeForCounties,
        )
      ? coercedCounties
      : [];

  const countiesPick = pickReportFieldValue(
    warehouseCounties,
    mockCounties,
    isWarehouse || hasCountyData(warehouseCounties),
  );

  const warehouseAge = coerceWhoWereReachingDistribution(snapshot.age);
  const mockAge = coerceIllustrativeDistributionFromPctRows(mockBase.age);
  const agePick = pickReportFieldValue(
    warehouseAge,
    mockAge,
    isWarehouse || hasDistributionData(warehouseAge),
  );

  const warehouseRace = coerceWhoWereReachingDistribution(snapshot.raceEthnicity);
  const mockRace = coerceIllustrativeDistributionFromPctRows(
    mockBase.raceEthnicity,
  );
  const racePick = pickReportFieldValue(
    warehouseRace,
    mockRace,
    isWarehouse || hasDistributionData(warehouseRace),
  );

  const mockEngagedTotal =
    typeof mockBase.engagedTotal === 'number' &&
    Number.isFinite(mockBase.engagedTotal)
      ? mockBase.engagedTotal
      : 0;
  const engagedPick = pickReportFieldValue(
    snapshotEngagedTotal ?? 0,
    mockEngagedTotal,
    isWarehouse,
  );

  const fieldSources: ReportSectionFieldSources = {
    counties: countiesPick.source,
    age: agePick.source,
    raceEthnicity: racePick.source,
    engagedTotal: engagedPick.source,
  };

  return {
    ...mockBase,
    ...snapshot,
    usStateCode,
    engagedTotal: engagedPick.value,
    counties: countiesPick.value,
    age: agePick.value,
    raceEthnicity: racePick.value,
    fieldSources,
  };
};

export {
  coerceWhoWereReachingCounties,
  coerceWhoWereReachingMemberCount,
  isWarehouseWhoWereReachingSnapshot,
  WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
} from './whoWereReachingSnapshotDetect.util';
